import {
    Button,
    Checkbox,
    NumberInput,
    Textarea,
    TextInput,
} from '@mantine/core';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { CreateOfferDto } from 'api/dtos/offer';

import styles from './OfferForm.module.scss';

export type OfferFormValues = CreateOfferDto;

interface Props {
    onSubmit: (data: OfferFormValues) => Promise<void>;
    isLoading: boolean;
    hideActions?: boolean;
    onDisabledChange?: (disabled: boolean) => void;
    initialValues?: Partial<OfferFormValues>;
}

export type OfferFormHandle = {
    submit: () => Promise<void>;
};

const OfferForm = forwardRef<OfferFormHandle, Props>(({
    onSubmit,
    isLoading,
    hideActions = false,
    onDisabledChange,
    initialValues,
}, ref) => {
    const intl = useIntl();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const canAddImage = isAdmin || isModerator || isOwner;

    const [name, setName] = useState(initialValues?.name ?? '');
    const [companyName, setCompanyName] = useState(initialValues?.companyName ?? '');
    const [description, setDescription] = useState(initialValues?.description ?? '');
    const [originalPrice, setOriginalPrice] = useState<number | string>(initialValues?.originalPrice ?? '');
    const [discountedPrice, setDiscountedPrice] = useState<number | string>(initialValues?.discountedPrice ?? '');
    const [offerUrl, setOfferUrl] = useState(initialValues?.offerUrl ?? '');
    const [imageUrl, setImageUrl] = useState(initialValues?.imageUrl ?? '');
    const [startAt, setStartAt] = useState(initialValues?.startAt ?? '');
    const [endAt, setEndAt] = useState(initialValues?.endAt ?? '');
    const [applyToChildren, setApplyToChildren] = useState(initialValues?.applyToChildren ?? false);
    const [formError, setFormError] = useState('');

    const isDisabled = useMemo(() => {
        const original = Number(originalPrice);
        const discounted = Number(discountedPrice);
        if (!name.trim() || !description.trim()) return true;
        if (Number.isNaN(original) || Number.isNaN(discounted) || original < 0 || discounted < 0) return true;
        if (startAt && endAt && new Date(startAt) > new Date(endAt)) return true;
        return false;
    }, [name, description, originalPrice, discountedPrice, startAt, endAt]);

    useEffect(() => {
        onDisabledChange?.(isDisabled);
    }, [isDisabled, onDisabledChange]);

    const handleSubmit = async () => {
        if (isDisabled) return;
        setFormError('');

        if (startAt && endAt && new Date(startAt) > new Date(endAt)) {
            setFormError(intl.formatMessage(messages.startEndDate));
            return;
        }

        const payload: OfferFormValues = {
            name: name.trim(),
            description: description.trim(),
            companyName: companyName.trim(),
            originalPrice: Number(originalPrice),
            discountedPrice: Number(discountedPrice),
            offerUrl: offerUrl.trim(),
            imageUrl: canAddImage ? imageUrl.trim() : '',
            startAt: startAt ? new Date(startAt).toISOString() : undefined,
            endAt: endAt ? new Date(endAt).toISOString() : undefined,
            applyToChildren,
        };

        await onSubmit(payload);
    };

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    return (
        <div className={styles.container}>
            <TextInput
                id="offer-name"
                label={intl.formatMessage(messages.name)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                required
                radius="md"
                size="md"
            />
            <TextInput
                id="offer-company"
                label={intl.formatMessage(messages.company)}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                value={companyName}
                maxLength={120}
                onChange={(e) => setCompanyName(e.target.value)}
                radius="md"
                size="md"
            />
            <Textarea
                id="offer-description"
                className={styles.fullWidth}
                label={intl.formatMessage(messages.description)}
                placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
                value={description}
                maxLength={800}
                onChange={(e) => setDescription(e.target.value)}
                minRows={4}
                autosize
                required
                radius="md"
                size="md"
            />
            <div className={styles.pricesRow}>
                <NumberInput
                    id="offer-original-price"
                    label={intl.formatMessage(messages.originalPrice)}
                    placeholder={intl.formatMessage(messages.originalPricePlaceholder)}
                    min={0}
                    decimalScale={2}
                    decimalSeparator=","
                    hideControls
                    value={originalPrice}
                    onChange={setOriginalPrice}
                    radius="md"
                    size="md"
                />
                <NumberInput
                    id="offer-discounted-price"
                    label={intl.formatMessage(messages.discountedPrice)}
                    placeholder={intl.formatMessage(messages.discountedPricePlaceholder)}
                    min={0}
                    decimalScale={2}
                    decimalSeparator=","
                    hideControls
                    value={discountedPrice}
                    onChange={setDiscountedPrice}
                    radius="md"
                    size="md"
                />
            </div>
            <TextInput
                id="offer-url"
                className={styles.fullWidth}
                label={intl.formatMessage(messages.offerUrl)}
                placeholder={intl.formatMessage(messages.offerUrlPlaceholder)}
                value={offerUrl}
                maxLength={250}
                onChange={(e) => setOfferUrl(e.target.value)}
                radius="md"
                size="md"
            />
            <TextInput
                id="offer-image-url"
                className={styles.fullWidth}
                label={intl.formatMessage(messages.imageUrl)}
                placeholder={intl.formatMessage(messages.imageUrlPlaceholder)}
                value={imageUrl}
                maxLength={250}
                disabled={!canAddImage}
                onChange={(e) => setImageUrl(e.target.value)}
                radius="md"
                size="md"
            />
            {!canAddImage && (
                <div className={styles.permissionNotice}>
                    {intl.formatMessage(messages.imagePermissionNotice)}
                </div>
            )}
            <TextInput
                id="offer-start-at"
                label={intl.formatMessage(messages.startAt)}
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                radius="md"
                size="md"
            />
            <TextInput
                id="offer-end-at"
                label={intl.formatMessage(messages.endAt)}
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                radius="md"
                size="md"
            />
            <div className={styles.checkboxContainer}>
                <Checkbox
                    id="offer-apply-to-children"
                    label={intl.formatMessage(messages.applyToChildren)}
                    checked={applyToChildren}
                    onChange={(e) => setApplyToChildren(e.currentTarget.checked)}
                    radius="sm"
                />
            </div>
            {formError && <div className={styles.formError}>{formError}</div>}
            {!hideActions && (
                <div className={styles.actions}>
                    <Button onClick={handleSubmit} disabled={isDisabled || isLoading} radius="md">
                        {intl.formatMessage(sharedMessages.save)}
                    </Button>
                </div>
            )}
        </div>
    );
});

const messages = defineMessages({
    name: {
        id: 'offers.form.name',
        defaultMessage: 'Nazwa oferty',
    },
    company: {
        id: 'offers.form.company',
        defaultMessage: 'Nazwa firmy',
    },
    namePlaceholder: {
        id: 'offers.form.namePlaceholder',
        defaultMessage: 'Np. Zniżka na warsztaty rodzinne',
    },
    companyPlaceholder: {
        id: 'offers.form.companyPlaceholder',
        defaultMessage: 'Np. Dom Kultury Świętej Anny',
    },
    description: {
        id: 'offers.form.description',
        defaultMessage: 'Opis oferty',
    },
    descriptionPlaceholder: {
        id: 'offers.form.descriptionPlaceholder',
        defaultMessage: 'Krótko opisz, czego dotyczy oferta i kto może z niej skorzystać',
    },
    originalPrice: {
        id: 'offers.form.originalPrice',
        defaultMessage: 'Cena oryginalna',
    },
    originalPricePlaceholder: {
        id: 'offers.form.originalPricePlaceholder',
        defaultMessage: 'Np. 199,00',
    },
    discountedPrice: {
        id: 'offers.form.discountedPrice',
        defaultMessage: 'Cena promocyjna',
    },
    discountedPricePlaceholder: {
        id: 'offers.form.discountedPricePlaceholder',
        defaultMessage: 'Np. 149,00',
    },
    offerUrl: {
        id: 'offers.form.offerUrl',
        defaultMessage: 'Link do oferty',
    },
    offerUrlPlaceholder: {
        id: 'offers.form.offerUrlPlaceholder',
        defaultMessage: 'https://example.com/oferta',
    },
    imageUrl: {
        id: 'offers.form.imageUrl',
        defaultMessage: 'Adres URL obrazu',
    },
    imageUrlPlaceholder: {
        id: 'offers.form.imageUrlPlaceholder',
        defaultMessage: 'https://example.com/image.png',
    },
    imagePermissionNotice: {
        id: 'offers.form.imagePermissionNotice',
        defaultMessage: 'Nie masz uprawnień do dodawania obrazu.',
    },
    startAt: {
        id: 'offers.form.startAt',
        defaultMessage: 'Data rozpoczęcia',
    },
    endAt: {
        id: 'offers.form.endAt',
        defaultMessage: 'Data zakończenia',
    },
    applyToChildren: {
        id: 'offers.form.applyToChildren',
        defaultMessage: 'Widoczne dla parafian',
    },
    startEndDate: {
        id: 'offers.form.startEndDate',
        defaultMessage: 'Data rozpoczęcia musi być wcześniejsza niż data zakończenia.',
    },
});

export default OfferForm;
